import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ReadinessJourney } from '@/pages/compliance/SetupGuidePage'
import { TrendChart } from '@/pages/screenata/readiness/components/TrendChart'
import { PrioritySignalIcon, PRIORITY_STYLE } from '@/pages/tasks/TaskCard'
import { DEFAULT_CATEGORY_VALUES } from '@/pages/tasks/TasksViewState'
import { getActionItemRoute } from '@/pages/tasks/actionItemRoute'
import type { ActionItem } from '@/pages/tasks/types'
import { Button } from '@deepguide-ai/ui/button'
import { PageEmptyState } from '@deepguide-ai/ui/page-empty-state'
import { Skeleton } from '@deepguide-ai/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@deepguide-ai/ui/tabs'
import { useAtomValue } from 'jotai'
import {
  Activity,
  UserPlus,
  Zap,
  Calendar,
  ListTodo,
  Boxes,
  Building2,
  FileText,
  ClipboardList,
  Plug,
  ShieldCheck,
  ShieldAlert,
  Users,
} from 'lucide-react'
import { toast } from 'sonner'
import { currentUserAtom } from '@/stores/sessionStore'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import { useGuidedPath } from '@/hooks/useGuidedPath'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import { AuditLogsTable } from '@/components/AuditLogsTable'
import { AuditCycleRow } from '@/components/auditor-center/AuditorCenterPage'
import { useAskAi } from '@/components/chat/useAskAi'
import { MemberAvatar } from '@/components/compliance/MemberAvatar'
import { RadialPercent } from '@/components/compliance/RadialPercent'
import { TaskStatusIcon } from '@/components/compliance/TaskStatusIcon'

function useDashboardQuery(workspaceId: string) {
  const options = trpc.complianceProgram.getDashboard.queryOptions()
  return useQuery({
    ...options,
    queryKey: [...options.queryKey, { workspaceId }],
    staleTime: 60_000,
    refetchInterval: 60_000,
  })
}

/** Compute a whole-number percentage, or null when there's no denominator. */
function ratioPct(numerator: number, denominator: number): number | null {
  if (denominator <= 0) return null
  return Math.round((numerator / denominator) * 100)
}

function StatCard({
  icon: Icon,
  label,
  value,
  description,
  percent,
  to,
  workspaceId,
}: {
  icon: React.ElementType
  label: string
  value: string
  description: string
  percent?: number | null
  to?: string
  workspaceId: string
}) {
  const content = (
    <div
      className={cn(
        'border-border bg-card flex h-full items-start justify-between gap-2 rounded-md border p-4',
        to &&
          'group-hover:border-cobalt-border group-hover:bg-cobalt-hover-faint transition-colors duration-200'
      )}
    >
      <div className='flex min-w-0 flex-col'>
        <div className='text-text-subtle flex items-center gap-2'>
          <Icon
            className={cn(
              'h-4 w-4 shrink-0',
              to && 'group-hover:text-cobalt-strong transition-colors'
            )}
          />
          <span className='text-[10px] font-medium tracking-wide uppercase'>
            {label}
          </span>
        </div>
        <p className='text-text-secondary mt-3 text-[22px] leading-none font-bold'>
          {value}
        </p>
        <p className='text-text-subtle mt-1.5 text-[12px]'>{description}</p>
      </div>
      {percent != null && <RadialPercent pct={percent} size={44} showPercent />}
    </div>
  )
  if (!to) return content
  return (
    <Link
      to={to as never}
      params={{ workspaceId }}
      preload={false}
      className='group focus-visible:ring-ring/25 block rounded-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none'
    >
      {content}
    </Link>
  )
}

function DashboardSkeleton() {
  return (
    <>
      <section
        aria-label='Loading readiness metrics'
        aria-busy='true'
        className='order-3 min-w-0 space-y-5 xl:order-2'
      >
        <div aria-hidden='true' className='bg-card rounded-md border p-5'>
          <div className='flex flex-col items-center gap-3'>
            <Skeleton className='h-3 w-28' />
            <Skeleton className='h-10 w-20' />
          </div>
          <Skeleton className='mt-9 h-[150px] w-full' />
        </div>
        <div
          aria-hidden='true'
          className='bg-card space-y-5 rounded-md border p-4'
        >
          <Skeleton className='h-5 w-24' />
          <Skeleton className='h-36 w-full' />
          <Skeleton className='h-36 w-full' />
        </div>
        <div aria-hidden='true' className='grid grid-cols-2 gap-4'>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className='h-[104px] rounded-md' />
          ))}
        </div>
      </section>
      <section
        aria-label='Loading tasks and activity'
        aria-busy='true'
        className='order-2 min-w-0 space-y-5 xl:order-3'
      >
        <div
          aria-hidden='true'
          className='bg-card space-y-5 rounded-md border p-4'
        >
          <Skeleton className='h-5 w-24' />
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className='flex items-center gap-3 border-b pb-4 last:border-b-0'
            >
              <Skeleton className='size-4 shrink-0 rounded-full' />
              <div className='flex-1 space-y-2'>
                <Skeleton className='h-4 w-full' />
                <Skeleton className='h-3 w-24' />
              </div>
              <Skeleton className='size-5 shrink-0 rounded-full' />
            </div>
          ))}
        </div>
        <div
          aria-hidden='true'
          className='bg-card space-y-4 rounded-md border p-4'
        >
          <Skeleton className='h-5 w-28' />
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className='h-12 w-full' />
          ))}
        </div>
      </section>
    </>
  )
}

function useHomeQueries(workspaceId: string) {
  const dashboardQuery = useDashboardQuery(workspaceId)
  const guide = useGuidedPath()
  const programId = dashboardQuery.data?.programId ?? guide.snapshot?.programId
  const cyclesOptions = trpc.auditor.listAuditCycles.queryOptions()
  const cyclesQuery = useQuery({
    ...cyclesOptions,
    queryKey: [...cyclesOptions.queryKey, { workspaceId }],
    enabled: !!programId,
    staleTime: 60_000,
  })
  // Readiness comes from the SAME live engine the chat home uses
  // (complianceProgram.getReadiness, RFC-135) so the two surfaces never show
  // different numbers. The dashboard payload carries its own snapshot too, but
  // the auditor center owns that; the workspace home tracks the live score.
  const readinessQuery = useQuery({
    ...trpc.complianceProgram.getReadiness.queryOptions({
      programId: programId ?? '',
    }),
    enabled: !!programId,
    staleTime: 60_000,
    refetchInterval: 60_000,
  })
  const trendQuery = useQuery({
    ...trpc.complianceProgram.getReadinessTrend.queryOptions({
      programId: programId ?? '',
      days: 90,
    }),
    enabled: !!programId,
    staleTime: 60_000,
  })
  // Recent activity reads the customer audit logs (latest 10). Lower plans
  // get the recent feed; Pro gets longer history; Enterprise retains export controls.
  const auditOptions = trpc.audit.getLogs.queryOptions({ limit: 10, offset: 0 })
  const auditLogsQuery = useQuery({
    ...auditOptions,
    queryKey: [...auditOptions.queryKey, { workspaceId }],
    enabled: !!programId,
    retry: false,
    staleTime: 15_000,
    refetchInterval: 15_000,
  })
  return {
    dashboardQuery,
    guide,
    cyclesQuery,
    readinessQuery,
    trendQuery,
    auditLogsQuery,
  }
}

export function HomeDashboard({ workspaceId }: { workspaceId: string }) {
  const { isAdmin, isAuditor, isLoading, canView } =
    useWorkspacePermissions(workspaceId)
  if (isLoading)
    return <Skeleton className='mx-auto mt-16 h-36 w-full max-w-[860px]' />
  if (isAdmin || isAuditor)
    return <AdminHomeDashboard workspaceId={workspaceId} />
  return canView ? <MemberHomeDashboard workspaceId={workspaceId} /> : null
}

function MemberHomeDashboard({ workspaceId }: { workspaceId: string }) {
  const options = trpc.complianceProgram.getProgram.queryOptions()
  const program = useQuery({
    ...options,
    queryKey: [...options.queryKey, { workspaceId }],
    staleTime: 60_000,
  })
  return (
    <div className='mx-auto w-full max-w-[860px] px-4 pt-16 pb-12'>
      {program.data ? (
        <HomeTasks workspaceId={workspaceId} programId={program.data.id} />
      ) : program.isPending ? (
        <Skeleton className='h-36 w-full' />
      ) : program.isError ? (
        <PageEmptyState
          icon={ListTodo}
          title='Could not load your tasks'
          actions={
            <Button variant='outline' onClick={() => void program.refetch()}>
              Retry
            </Button>
          }
        />
      ) : (
        <PageEmptyState icon={ListTodo} title='No compliance program yet' />
      )}
    </div>
  )
}

function AdminHomeDashboard({ workspaceId }: { workspaceId: string }) {
  const queries = useHomeQueries(workspaceId)
  const { dashboardQuery } = queries
  const dashboard = dashboardQuery.data
  const noProgram = dashboardQuery.error?.data?.code === 'NOT_FOUND'
  return (
    <div className='mx-auto w-full max-w-[1360px] px-4 pt-16 pb-12 sm:px-6 lg:pt-20'>
      {dashboard ? (
        <DashboardContent
          dashboard={dashboard}
          workspaceId={workspaceId}
          queries={queries}
        />
      ) : (
        <div className='grid items-start gap-5 xl:grid-cols-3'>
          <ReadinessJourney />
          {dashboardQuery.isPending ? (
            <DashboardSkeleton />
          ) : (
            <div className='order-2 min-w-0 xl:col-span-2'>
              {dashboardQuery.isError && !noProgram ? (
                <PageEmptyState
                  icon={ShieldAlert}
                  title='Could not load the dashboard'
                  description='Try again to refresh your compliance overview.'
                  actions={
                    <Button
                      variant='outline'
                      onClick={() => void dashboardQuery.refetch()}
                    >
                      Retry
                    </Button>
                  }
                />
              ) : (
                <PageEmptyState
                  icon={ShieldCheck}
                  title='No compliance program yet'
                  description='Set up your program in the readiness journey to get started.'
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function DashboardContent({
  dashboard,
  workspaceId,
  queries,
}: {
  dashboard: NonNullable<ReturnType<typeof useDashboardQuery>['data']>
  workspaceId: string
  queries: ReturnType<typeof useHomeQueries>
}) {
  const { stats, blockers, warnings } = dashboard
  const { guide, cyclesQuery, readinessQuery, trendQuery, auditLogsQuery } =
    queries
  const activeCycles = (cyclesQuery.data ?? []).filter(
    (cycle) => cycle.status !== 'completed'
  )
  const counters = readinessQuery.data?.counters
  // The canonical endpoint already excludes N/A from testsTotal.
  const noApplicableTests = counters != null && counters.testsTotal === 0
  const overall =
    readinessQuery.data?.overall != null && !noApplicableTests
      ? Math.round(readinessQuery.data.overall)
      : null

  // Readiness-over-time trend. Stored daily snapshots (ReadinessSnapshot) drive
  // the history; we append the LIVE overall as today's point so the chart's
  // endpoint always agrees with the hero number above — the stored snapshots
  // use an older scoring formula and the last one is only as fresh as the daily
  // snapshot job. Any snapshot already dated today is dropped first so we don't
  // plot two points on the same day.
  const todayKey = new Date().toISOString().slice(0, 10)
  const trendData = [
    ...(trendQuery.data ?? [])
      .map((s) => ({
        overall: s.overall,
        snapshotAt: new Date(s.snapshotAt).toISOString(),
      }))
      .filter((s) => s.snapshotAt.slice(0, 10) !== todayKey),
    ...(overall != null
      ? [{ overall, snapshotAt: new Date().toISOString() }]
      : []),
  ]

  const coverageSteps = guide.chapters.flatMap((chapter) => chapter.steps)

  const auditLogs = auditLogsQuery.data?.logs ?? []

  return (
    <div className='space-y-6'>
      <div className='grid items-start gap-5 xl:grid-cols-3'>
        <ReadinessJourney
          controls={{
            done: stats.completeControls,
            total: stats.totalControls,
          }}
        />
        <section
          aria-label='Readiness metrics'
          className='order-3 min-w-0 space-y-5 xl:order-2'
        >
          {/* Readiness hero */}
          <div className='bg-card border-border rounded-md border p-5'>
            <div className='space-y-5'>
              <div className='flex flex-col items-center justify-center text-center'>
                <span className='text-text-subtle text-[10px] font-medium tracking-wide uppercase'>
                  Overall readiness
                </span>
                {overall != null ? (
                  <span className='text-text-secondary mt-3 text-[36px] leading-none font-semibold'>
                    {overall}
                    <span className='text-text-subtle text-[20px]'>%</span>
                  </span>
                ) : (
                  <div
                    className='text-text-subtle mt-2 text-[13px]'
                    role='status'
                  >
                    {readinessQuery.isPending
                      ? 'Loading readiness…'
                      : readinessQuery.isError
                        ? 'Readiness unavailable'
                        : noApplicableTests
                          ? 'N/A · No applicable tests'
                          : 'No applicable tests yet'}
                    {readinessQuery.isError && (
                      <Button
                        variant='ghost'
                        size='sm'
                        onClick={() => void readinessQuery.refetch()}
                      >
                        Retry readiness
                      </Button>
                    )}
                  </div>
                )}
              </div>
              <div className='bg-card min-w-0 rounded-sm p-4'>
                <div>
                  {trendQuery.isError ? (
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={() => void trendQuery.refetch()}
                    >
                      Retry readiness trend
                    </Button>
                  ) : readinessQuery.isLoading || trendQuery.isLoading ? (
                    <Skeleton className='h-[150px] w-full rounded-sm' />
                  ) : (
                    <TrendChart data={trendData} heightClass='h-[150px]' />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Blockers / warnings */}
          {(blockers.length > 0 || warnings.length > 0) && (
            <div className='space-y-3'>
              {blockers.length > 0 && (
                <div className='border-status-red/30 bg-status-red-bg text-status-red rounded-sm border p-3 text-[13px]'>
                  {blockers.join(' ')}
                </div>
              )}
              {warnings.length > 0 && (
                <div className='border-status-orange/30 bg-status-orange-bg text-status-orange rounded-sm border p-3 text-[13px]'>
                  {warnings.join(' ')}
                </div>
              )}
            </div>
          )}

          <section
            aria-label='Audits'
            className='bg-card space-y-3 rounded-md border p-4'
          >
            <div className='flex items-center justify-between gap-3'>
              <h2 className='flex items-center gap-2 text-sm font-semibold'>
                <ShieldCheck
                  aria-hidden='true'
                  className='text-muted-foreground size-4'
                />
                Audits
              </h2>
              <Link
                to='/workspaces/$workspaceId/audits'
                params={{ workspaceId }}
                className='text-cobalt-strong text-xs hover:underline'
              >
                View audits →
              </Link>
            </div>
            {cyclesQuery.isPending ? (
              <Skeleton className='h-56 w-full rounded-md' />
            ) : cyclesQuery.isError ? (
              <div className='text-muted-foreground py-4 text-xs' role='status'>
                Audit cycles unavailable.{' '}
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => void cyclesQuery.refetch()}
                >
                  Retry audit cycles
                </Button>
              </div>
            ) : activeCycles.length === 0 ? (
              <p className='text-muted-foreground py-4 text-xs'>
                No active audit cycles. Create one in Audits to track your
                engagement.
              </p>
            ) : (
              <div className='divide-y'>
                {activeCycles.map((cycle) => (
                  <AuditCycleRow
                    key={cycle.id}
                    cycle={cycle}
                    variant='row'
                    workspaceId={workspaceId}
                  />
                ))}
              </div>
            )}
          </section>

          {/* Primary stat cards */}
          <div className='grid grid-cols-2 gap-3'>
            <StatCard
              icon={FileText}
              label='Policies'
              value={`${stats.verifiedPolicies}/${stats.totalPolicies}`}
              description='Approved'
              percent={ratioPct(stats.verifiedPolicies, stats.totalPolicies)}
              to='/workspaces/$workspaceId/policies'
              workspaceId={workspaceId}
            />
            <StatCard
              icon={Users}
              label='People'
              value={`${stats.activeEmployeeCount}/${stats.employeeCount}`}
              description='Active employees'
              percent={ratioPct(stats.activeEmployeeCount, stats.employeeCount)}
              to='/workspaces/$workspaceId/people'
              workspaceId={workspaceId}
            />
            <StatCard
              icon={ShieldCheck}
              label='Controls'
              value={`${stats.completeControls}/${stats.totalControls}`}
              description='Approved controls'
              percent={ratioPct(stats.completeControls, stats.totalControls)}
              to='/workspaces/$workspaceId/controls'
              workspaceId={workspaceId}
            />
            {[
              {
                key: 'risks.treat',
                label: 'Risks',
                icon: ShieldAlert,
                description: 'Treatment in place',
                to: '/workspaces/$workspaceId/risks',
              },
              {
                key: 'vendors.assess',
                label: 'Vendors',
                icon: Building2,
                description: 'Assessed vendors',
                to: '/workspaces/$workspaceId/vendors',
              },
              {
                key: 'assets.owners',
                label: 'Assets',
                icon: Boxes,
                description: 'Assets with owners',
                to: '/workspaces/$workspaceId/assets',
              },
            ].map((metric) => {
              const coverage =
                !guide.isError && guide.isReady
                  ? coverageSteps.find((step) => step.key === metric.key)
                      ?.coverage
                  : undefined
              return (
                <StatCard
                  key={metric.key}
                  icon={metric.icon}
                  label={metric.label}
                  value={coverage ? `${coverage.done}/${coverage.total}` : '—'}
                  description={
                    guide.isError
                      ? 'Coverage unavailable'
                      : !guide.isReady
                        ? 'Loading coverage…'
                        : coverage
                          ? metric.description
                          : 'Coverage unavailable'
                  }
                  percent={
                    coverage ? ratioPct(coverage.done, coverage.total) : null
                  }
                  to={metric.to}
                  workspaceId={workspaceId}
                />
              )
            })}
          </div>
        </section>
        <section
          aria-label='Tasks and activity'
          className='order-2 min-w-0 space-y-5 xl:order-3'
        >
          <HomeTasks
            workspaceId={workspaceId}
            programId={dashboard.programId}
          />
          <HomeActions workspaceId={workspaceId} />
          <div className='bg-card rounded-md border p-4'>
            <div className='mb-4 flex items-center justify-between gap-3'>
              <h2 className='flex items-center gap-2 text-sm font-semibold'>
                <Activity
                  aria-hidden='true'
                  className='text-muted-foreground size-4'
                />
                Recent activity
              </h2>
              <Link
                to='/workspaces/$workspaceId/settings/audit-logs'
                params={{ workspaceId }}
                className='text-cobalt-strong text-xs hover:underline'
              >
                View all →
              </Link>
            </div>
            {auditLogsQuery.isPending ? (
              <Skeleton className='h-36 w-full' />
            ) : auditLogsQuery.isError ? (
              <div role='status' className='text-muted-foreground text-xs'>
                Activity unavailable.{' '}
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => void auditLogsQuery.refetch()}
                >
                  Retry activity
                </Button>
              </div>
            ) : auditLogs.length === 0 ? (
              <p className='text-muted-foreground text-xs'>
                No audit activity recorded yet.
              </p>
            ) : (
              <AuditLogsTable
                logs={auditLogs}
                variant='feed'
                workspaceId={workspaceId}
              />
            )}
          </div>
        </section>
      </div>
    </div>
  )
}

function HomeActions({ workspaceId }: { workspaceId: string }) {
  const askAi = useAskAi()
  const [isOpeningReport, setIsOpeningReport] = useState(false)
  const actionClass =
    'focus-visible:outline-ring hover:bg-muted/50 flex min-h-28 min-w-0 w-full flex-col items-center justify-center gap-3 rounded-sm px-2 py-4 text-center text-xs leading-5 transition-colors focus-visible:outline-2 [&>svg]:size-8 [&>svg]:shrink-0'
  return (
    <section aria-label='Actions' className='bg-card rounded-md border p-4'>
      <h2 className='mb-4 flex items-center gap-2 text-sm font-semibold'>
        <Zap aria-hidden='true' className='text-muted-foreground size-4' />
        Actions
      </h2>
      <div className='grid grid-cols-2 gap-2'>
        <Button
          variant='ghost'
          type='button'
          className={cn(
            actionClass,
            'h-auto whitespace-normal disabled:cursor-wait has-[>svg]:px-2'
          )}
          disabled={isOpeningReport}
          onClick={async () => {
            setIsOpeningReport(true)
            try {
              await askAi({
                text: 'Create a compliance posture report for our board covering the last 30 days, with readiness trends, a risk heat map, priorities, and source dates. Prepare it as a Vault Document for me to review.',
                threadName: 'Board report',
                sendMessage: false,
              })
            } catch (error) {
              toast.error(
                error instanceof Error
                  ? error.message
                  : 'Could not open Vera. Please try again.'
              )
            } finally {
              setIsOpeningReport(false)
            }
          }}
        >
          <FileText
            aria-hidden='true'
            className='text-muted-foreground size-8 shrink-0'
          />
          <span className='flex min-h-10 items-center justify-center font-medium'>
            {isOpeningReport ? 'Opening Vera…' : 'Create board report'}
          </span>
        </Button>
        <Link
          to='/workspaces/$workspaceId/settings/members'
          params={{ workspaceId }}
          className={actionClass}
        >
          <UserPlus
            aria-hidden='true'
            className='text-muted-foreground size-8 shrink-0'
          />
          <span className='flex min-h-10 items-center justify-center font-medium'>
            Invite members
          </span>
        </Link>
        <Link
          to='/workspaces/$workspaceId/questionnaires'
          params={{ workspaceId }}
          className={actionClass}
        >
          <ClipboardList
            aria-hidden='true'
            className='text-muted-foreground size-8 shrink-0'
          />
          <span className='flex min-h-10 items-center justify-center font-medium'>
            Answer questionnaire
          </span>
        </Link>
        <Link
          to='/workspaces/$workspaceId/integrations'
          params={{ workspaceId }}
          className={actionClass}
        >
          <Plug
            aria-hidden='true'
            className='text-muted-foreground size-8 shrink-0'
          />
          <span className='flex min-h-10 items-center justify-center font-medium'>
            Add integration
          </span>
        </Link>
      </div>
    </section>
  )
}

function HomeTasks({
  workspaceId,
  programId,
}: {
  workspaceId: string
  programId: string
}) {
  const user = useAtomValue(currentUserAtom)
  const [filter, setFilter] = useState('mine')
  const query = useQuery({
    ...trpc.task.board.queryOptions({
      programId,
      category: [...DEFAULT_CATEGORY_VALUES],
      includeResolved: false,
      limit: 5,
      ...(filter === 'mine' ? { assignedTo: user?.id } : {}),
    }),
    enabled: filter === 'all' || !!user?.id,
    staleTime: 15_000,
    refetchInterval: 15_000,
  })
  const tasks = (query.data?.items ?? []) as ActionItem[]
  return (
    <section className='bg-card rounded-md border p-4' aria-label='Tasks'>
      <div className='mb-3 flex flex-wrap items-center justify-between gap-2'>
        <h2 className='flex items-center gap-2 text-sm font-semibold'>
          <ListTodo
            aria-hidden='true'
            className='text-muted-foreground size-4'
          />
          Tasks
        </h2>
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList>
            <TabsTrigger value='mine'>Mine</TabsTrigger>
            <TabsTrigger value='all'>All</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      {query.isPending ? (
        <Skeleton className='h-36 w-full' />
      ) : query.isError ? (
        <p role='status' className='text-muted-foreground text-xs'>
          Tasks unavailable.{' '}
          <Button
            variant='ghost'
            size='sm'
            onClick={() => void query.refetch()}
          >
            Retry tasks
          </Button>
        </p>
      ) : tasks.length === 0 ? (
        <p className='text-muted-foreground py-4 text-xs'>
          {filter === 'mine'
            ? 'No active tasks assigned to you.'
            : 'No active tasks.'}
        </p>
      ) : (
        <ul className='divide-y'>
          {tasks.map((task) => (
            <li key={`${task.category}:${task.id}`}>
              <Link
                {...getActionItemRoute(task, workspaceId)}
                className='focus-visible:outline-ring hover:text-cobalt-strong block rounded-sm py-3 text-xs focus-visible:outline-2'
              >
                <span className='flex items-center gap-2'>
                  <TaskStatusIcon status={task.taskStatus} />
                  <PrioritySignalIcon
                    priority={task.priority}
                    className={cn('shrink-0', PRIORITY_STYLE[task.priority])}
                  />
                  <span className='min-w-0 flex-1 font-medium break-words'>
                    {task.title} ↗
                  </span>
                  <span
                    title={task.assignedTo?.name ?? 'Unassigned'}
                    className='shrink-0'
                  >
                    <MemberAvatar
                      person={
                        task.assignedTo
                          ? { ...task.assignedTo, isAgent: task.isAgent }
                          : { name: 'Unassigned' }
                      }
                      size='xs'
                    />
                  </span>
                </span>
                <span className='text-muted-foreground mt-1 flex items-center gap-1 pl-[46px] text-[10px]'>
                  <span className='inline-flex items-center gap-1'>
                    <Calendar aria-hidden='true' className='size-3' />
                    {task.dueDate
                      ? `Due ${new Date(task.dueDate).toLocaleDateString()}`
                      : 'No due date'}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
      <Link
        to='/workspaces/$workspaceId/tasks'
        params={{ workspaceId }}
        className='text-cobalt-strong mt-3 block border-t pt-3 text-xs hover:underline'
      >
        View all tasks →
      </Link>
    </section>
  )
}
